/**
 * Task List Page
 * TASK-359: TaskListPage & Dashboard Integration
 *
 * Full task management page: filters, stats and the status board.
 *
 * TASK-685 review: the page is the BOARD. The list⇄board toggle is gone — the review found
 * the board unfindable behind it (list was the default, the toggle was the quietest of three
 * header buttons), and a `review` column nobody reaches delivers none of its value. One
 * rendering, no view-mode state, no second grouping of the same rows.
 */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ListTodo,
  Plus,
  CheckCircle,
  AlertCircle,
  Clock,
  Calendar,
  RefreshCw,
} from 'lucide-react';
import SecondaryNav from '../components/layout/SecondaryNav';
import TaskBoard from '../components/tasks/TaskBoard';
import type { TaskOutcomeSubmission } from '../components/tasks/TaskOutcomePicker';
import TaskFilters from '../components/tasks/TaskFilters';
import AddTaskModal from '../components/tasks/AddTaskModal';
import { WidgetErrorState } from '../components/ui/WidgetErrorState';
import {
  useTasks,
  useTaskDashboardStats,
  useCompleteTask,
  useSnoozeTask,
} from '../hooks/useTasksQuery';
import { TaskListFilters, Task, TaskCreateRequest, TaskStatus } from '../types/tasks';
import { taskService } from '../services/taskService';
import { toast } from 'sonner';

const TaskListPage: React.FC = () => {
  // Modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  // Filters state
  const [filters, setFilters] = useState<TaskListFilters>({
    status: undefined,
    sort_by: 'due_date',
    sort_order: 'asc',
    limit: 100,
  });

  // TASK-685 review: `?highlight=<task-id>` — where TasksAwaitingReview sends the RM when they
  // click a row. It used to be written and read by nobody, so the click landed on an
  // undifferentiated board.
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');

  // Fetch tasks and stats
  const { data: tasksData, isLoading: isLoadingTasks, isError: isTasksError, refetch: refetchTasks } = useTasks(filters);
  const { data: statsData } = useTaskDashboardStats();

  // Mutations
  const completeTaskMutation = useCompleteTask();
  const snoozeTaskMutation = useSnoozeTask();

  // Handle task creation or update
  const handleCreateTask = async (taskData: TaskCreateRequest) => {
    setIsCreatingTask(true);
    try {
      if (editingTask) {
        // Update existing task
        await taskService.updateTask(editingTask.id, {
          ...taskData,
          version: editingTask.version,
        });
        toast.success('Task updated successfully');
      } else {
        // Create new task
        await taskService.createTask(taskData);
        toast.success('Task created successfully');
      }
      setIsAddModalOpen(false);
      setEditingTask(null);
      refetchTasks();
    } catch (error: any) {
      console.error('Error saving task:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to save task';
      toast.error(errorMessage);
    } finally {
      setIsCreatingTask(false);
    }
  };

  // Handle task completion
  const handleCompleteTask = async (taskId: string, submission?: TaskOutcomeSubmission) => {
    try {
      // TASK-922: signal-bound work carries its outcome; the server 400s without one.
      await completeTaskMutation.mutateAsync(
        submission ? { taskId, ...submission } : taskId,
      );
      toast.success('Task completed');
    } catch (error: any) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    }
  };

  // Handle task snooze
  const handleSnoozeTask = async (taskId: string, snoozeUntil: string) => {
    try {
      await snoozeTaskMutation.mutateAsync({ taskId, snoozeUntil });
      toast.success('Task snoozed');
    } catch (error: any) {
      console.error('Error snoozing task:', error);
      toast.error('Failed to snooze task');
    }
  };

  // TASK-685: a board drop is just a status PATCH — which writes the `status_changed`
  // task_event via the DB trigger (TASK-684), so there is nothing to record here.
  //
  // `version` comes from the row being dragged; the PATCH response carries the incremented
  // one (TASK-684 fixed the stale-version bug that made the SECOND drag 409). A 409 means
  // someone else moved it, so refetch rather than leave a card lying about where it is.
  const handleStatusChange = async (task: Task, status: TaskStatus) => {
    try {
      await taskService.updateTask(task.id, { status, version: task.version });
      refetchTasks();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        toast.error('That task was just changed elsewhere — refreshing.');
      } else {
        console.error('Error moving task:', error);
        toast.error('Failed to move task');
      }
      refetchTasks();
    }
  };

  // Handle edit task
  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    // TODO: Open edit modal (can reuse AddTaskModal with pre-filled data)
    setIsAddModalOpen(true);
  };

  const stats = statsData?.stats;

  return (
    <div className="min-h-screen bg-gray-50">
      <SecondaryNav />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ListTodo className="w-8 h-8 text-[#002B45]" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
              <p className="text-sm text-gray-600">
                Manage your tasks and follow-ups
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => refetchTasks()}
              className="inline-flex items-center px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-[#002B45] text-white text-sm font-medium rounded-md hover:bg-[#003d5c] transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Task
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-gray-500">Pending</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.pending_tasks}</p>
            </div>
            <div className="bg-white rounded-lg border border-red-200 p-4">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <span className="text-xs text-gray-500">Overdue</span>
              </div>
              <p className="text-2xl font-bold text-red-600">{stats.overdue_tasks}</p>
            </div>
            <div className="bg-white rounded-lg border border-amber-200 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-amber-600" />
                <span className="text-xs text-gray-500">Due This Week</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.tasks_due_this_week}</p>
            </div>
            <div className="bg-white rounded-lg border border-green-200 p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-xs text-gray-500">Completed Today</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{stats.completed_today}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <TaskFilters filters={filters} onFiltersChange={setFilters} />

        {/* Task List */}
        {isLoadingTasks ? (
          <div className="text-center py-12">
            <div className="inline-block w-6 h-6 border-2 border-[#002B45] border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-2 text-sm text-gray-600">Loading tasks...</p>
          </div>
        ) : isTasksError ? (
          // TASK-602: a failed request must not render as "No tasks found".
          <WidgetErrorState
            title="Couldn't load tasks"
            detail="The task service didn't respond."
            onRetry={() => void refetchTasks()}
            className="py-12"
          />
        ) : !tasksData?.tasks || tasksData.tasks.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <ListTodo className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No tasks found</h3>
            <p className="text-sm text-gray-600 mb-4">
              {Object.keys(filters).some((k) => filters[k as keyof TaskListFilters])
                ? 'Try adjusting your filters'
                : 'Create your first task to get started'}
            </p>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-[#002B45] text-white text-sm font-medium rounded-md hover:bg-[#003d5c] transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Task
            </button>
          </div>
        ) : (
          <div>
            <TaskBoard
              tasks={tasksData.tasks}
              highlightId={highlightId}
              onStatusChange={handleStatusChange}
              onComplete={handleCompleteTask}
              onSnooze={handleSnoozeTask}
              onEdit={handleEditTask}
            />

            {/* The 100-row limit is real: say so rather than let a truncated board read as
                the whole book. Lived in the list branch until the board became the page. */}
            {tasksData.total > tasksData.tasks.length && (
              <div className="text-center mt-6">
                <p className="text-sm text-gray-500">
                  Showing {tasksData.tasks.length} of {tasksData.total} tasks
                </p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Add/Edit Task Modal */}
      <AddTaskModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingTask(null);
        }}
        onSubmit={handleCreateTask}
        isLoading={isCreatingTask}
        editingTask={editingTask}
      />
    </div>
  );
};

export default TaskListPage;
